// File path : src\api\document.ts

import { MasterActivity } from 'types/design-studio';
import baseApi, { API_V1 } from './base';
import {
  DerivedlookupDetails,
  DocumentType,
  FieldDetails,
  MasterComponentsTypes,
  MasterComponentsWithMappingFieldType,
  TableDetails
} from 'types/document';
import { orderBy } from 'lodash';

const doucumentApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDocument: build.query({
      query: ({ ProcessId }) => ({
        url: `${API_V1}/Documents`,
        params: { ProcessId }
      }),
      transformResponse: (data: DocumentType[], meta, arg) => {
        return data.map((item) => {
          return { value: item.value, label: item.text };
        });
      }
    }),
    getFilterdDocumentForMutilpleProcess: build.query({
      query: ({ processId }) => {
        // Ensure processId is sent as a comma-separated string
        const processIdParam = Array.isArray(processId) ? processId.join(',') : processId;
        return {
          url: `${API_V1}/Reports/GetDocumentList`,
          params: { processIds: processIdParam }
        };
      },
      transformResponse: (data: any, meta, arg) => {
        return data;
      }
    }),
    getFilterdBatchListForMutilpleProcess: build.query({
      query: ({ documentId }) => {
        const documentIdParam = Array.isArray(documentId) ? documentId.join(',') : documentId;
        return {
          url: `${API_V1}/Reports/GetBatchList`,
          params: { DocTypeIds: documentIdParam }
        };
      },
      transformResponse: (data: any, meta, arg) => {
        return data;
      }
    }),

    getFilterdDocument: build.query({
      query: ({ processId }) => ({
        url: `${API_V1}/DocumentList`,
        params: { processId }
      }),
      transformResponse: (data: any, meta, arg) => {
        return data;
      }
    }),
    getModelManagementDocumentType: build.query({
      query: ({ processId }) => ({
        url: `${API_V1}/ModelManagement/GetModelManagementDocumentType`,
        params: { processId }
      }),
      transformResponse: (data: any, meta, arg) => {
        return data;
      }
    }),
    getComponentPropertyList: build.query({
      query: ({ ComponentID, ModuleName }) => ({
        url: `${API_V1}/DocumentTagging/GetComponentProperties`,
        params: { ComponentID, ModuleName }
      }),
      transformErrorResponse: (data: any, meta, arg) => {
        return data;
      }
    }),
    getFieldDetails: build.query({
      query: ({ fieldID, docTypeID }) => ({
        url: `${API_V1}/DocumentTagging/GetFieldDetails`,
        params: { fieldID, docTypeID }
      }),
      providesTags: ['fieldDetails' as any],
      transformResponse: (data: FieldDetails, meta, arg) => {
        return data;
      }
    }),
    getmastercomponent: build.query({
      query: ({ ComponentType }) => ({
        url: `${API_V1}/DocumentTagging/GetMasterComponents`,
        params: { ComponentType }
      }),
      transformResponse: (data: MasterActivity, meta, arg) => {
        return data;
      }
    }),
    getDocumentTaggingComponentPropertyList: build.query({
      query: () => ({
        url: `${API_V1}/DocumentTagging/GetTaggingMasterPropertyValues`
      }),
      transformErrorResponse: (data: any) => {
        return data;
      }
    }),
    getBatchDetails: build.query({
      query: ({ batchId }) => ({
        url: `${API_V1}/DocumentTagging/GetBatch`,
        params: { batchId }
      }),
      transformResponse: (data: FieldDetails, meta, arg) => {
        return data;
      }
    }),
    getMasterComponentWithMappingFieldType: build.query<MasterComponentsWithMappingFieldType[], MasterComponentsTypes | void>({
      query: (componentType = 'CMR') => ({
        url: `${API_V1}/DocumentTagging/GetMasterComponentsWithMappingFieldType`,
        params: { componentType }
      }),
      transformResponse: (responseData: { data: any[] }) => {
        const data: MasterComponentsWithMappingFieldType[] = [];
        if (responseData.data) {
          responseData.data.forEach((row: any) => {
            const validationTypes = row.dataType ? JSON.parse(row.dataType)[0] : {};
            let dataTypes = [];
            for (const id in validationTypes) {
              dataTypes.push({ id, name: validationTypes[id] });
            }
            dataTypes = orderBy(dataTypes, ['name'], ['asc']);
            delete row.dataType;
            data.push({ ...row, dataTypes });
          });
        }
        return data;
      }
    }),
    getTextForROI: build.query({
      query: ({ FileId, PageNo, Xaxis, Yaxis, Width, Height }) => ({
        url: `${API_V1}/DocumentUpload/GetTextForROI`,
        params: { FileId, PageNo, Xaxis, Yaxis, Width, Height }
      }),
      transformResponse: (data: FieldDetails, meta, arg) => {
        return data;
      }
    }),
    saveFieldMaster: build.mutation({
      query: (data) => ({
        url: `${API_V1}/FieldMaster`,
        method: 'POST',
        data: data
      }),
      invalidatesTags: ['fieldDetails', 'data-point-configuration' as any],
      transformErrorResponse: (response: any) => {
        return response; // Optional: Customize error handling here
      }
    }),
    updateFieldMaster: build.mutation({
      query: (data) => ({
        url: `${API_V1}/FieldMaster`,
        method: 'PUT',
        data: data
      }),
      invalidatesTags: ['fieldDetails', 'data-point-configuration' as any],
      transformErrorResponse: (response: any) => {
        return response; // Optional: Customize error handling here
      }
    }),
    deleteTechnique: build.mutation<void, { techID: number; docTypeID: number }>({
      query: ({ techID, docTypeID }) => ({
        url: `${API_V1}/FieldMaster/DeleteTechnique`,
        method: 'DELETE',
        params: { techniqueID: techID, docTypeID }
      }),
      invalidatesTags: ['fieldDetails' as any],
      onQueryStarted: async (arg, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Failed to delete technique:', arg, error);
        }
      }
    }),
    deleteField: build.mutation<void, { fieldID: number }>({
      query: ({ fieldID }) => ({
        url: `${API_V1}/FieldMaster`,
        method: 'DELETE',
        params: { fieldID }
      }),
      invalidatesTags: ['fieldDetails', 'data-point-configuration' as any],
      onQueryStarted: async (arg, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error('Failed to delete technique:', arg, error);
        }
      }
    }),
    getFieldDetailsOnEdit: build.query({
      query: ({ fieldID, docTypeID, techID, fileID }) => ({
        url: `${API_V1}/FieldMaster/GetFieldConfig`,
        params: { fieldID, docTypeID, techID, fileID }
      }),
      transformErrorResponse: (data: any, meta, arg) => {
        return data;
      }
    }),
    updateFieldNameFunction: build.mutation({
      query: ({ docTypeId, renameFieldName, existingFieldID }) => ({
        url: `${API_V1}/FieldMaster/RenameFieldName`,
        method: 'PUT',
        params: { docTypeId, renameFieldName, existingFieldID }
        // data: { docTypeId, renameFieldName, existingFieldID }
      }),
      transformErrorResponse: (response: any) => {
        return response; // Optional: Customize error handling here
      }
    }),
    getTableFieldDetails: build.query({
      query: ({ docTypeID }) => ({
        url: `${API_V1}/DocumentTagging/GetFieldDetailsForTableComponent`,
        params: { docTypeID }
      }),
      transformResponse: (data: TableDetails, meta, arg) => {
        return data;
      }
    }),
    getderivedLookupFieldDetails: build.query({
      query: ({ docTypeID }) => ({
        url: `${API_V1}/DocumentTagging/GetFieldDetailsForDerivedLookupComponent`,
        params: { docTypeID }
      }),
      transformResponse: (data: DerivedlookupDetails, meta, arg) => {
        return data;
      }
    })
  })
});

export const {
  useGetDocumentQuery,
  useGetFilterdDocumentQuery,
  useGetFilterdDocumentForMutilpleProcessQuery,
  useGetFilterdBatchListForMutilpleProcessQuery,
  useLazyGetComponentPropertyListQuery,
  useGetFieldDetailsQuery,
  useLazyGetmastercomponentQuery,
  useGetBatchDetailsQuery,
  useGetMasterComponentWithMappingFieldTypeQuery,
  useLazyGetTextForROIQuery,
  useSaveFieldMasterMutation,
  useUpdateFieldMasterMutation,
  useDeleteTechniqueMutation,
  useDeleteFieldMutation,
  useLazyGetFieldDetailsOnEditQuery,
  useUpdateFieldNameFunctionMutation,
  useGetTableFieldDetailsQuery,
  useLazyGetderivedLookupFieldDetailsQuery,
  useGetDocumentTaggingComponentPropertyListQuery,
  useGetModelManagementDocumentTypeQuery,
  useLazyGetModelManagementDocumentTypeQuery
} = doucumentApi;

export default doucumentApi;
